import { useEffect, useId, useMemo, useRef, useState, type KeyboardEvent, type ReactNode, type RefObject } from "react";
import { Braces, Check, Languages, PencilLine, RefreshCw } from "lucide-react";
import { buildBilingualPromptSections, pairBilingualPromptSentences, promptSectionLinks, tokenizePromptSyntax } from "./promptSyntax";

type TranslationState = "loading" | "ready" | "error";

type Props = {
  label: string;
  value: string;
  disabled?: boolean;
  textareaRef: RefObject<HTMLTextAreaElement | null>;
  translation: string;
  translationState: TranslationState;
  translationStale?: boolean;
  onChange: (value: string) => void;
  onRetryTranslation: () => void;
  onRequestEdit?: () => Promise<void>;
};

const LEGEND = [
  ["field", "구조 필드"],
  ["timestamp", "시간"],
  ["reference", "레퍼런스"],
  ["camera", "카메라"],
  ["continuity", "유지 조건"],
  ["constraint", "금지 조건"],
  ["sound", "사운드"],
] as const;

function SyntaxText({ value }: { value: string }) {
  return <>{tokenizePromptSyntax(value).map((token, index) => (
    token.kind === "plain"
      ? token.text
      : <mark className={`syntax-${token.kind}`} key={`${token.kind}-${index}`}>{token.text}</mark>
  ))}</>;
}

function KoreanText({ value }: { value: string }) {
  const pattern = /(<(?:Picture|Video) \d+>|\[Shot \d+\]|\d+(?:\.\d+)?초(?:부터|까지)?)/g;
  return <>{value.split(pattern).filter(Boolean).map((part, index): ReactNode => {
    if (/^<(?:Picture|Video) \d+>$|^\[Shot \d+\]$/.test(part)) {
      return <mark className="translation-token reference-token" key={`${part}-${index}`}>{part}</mark>;
    }
    if (/^\d+(?:\.\d+)?초(?:부터|까지)?$/.test(part)) {
      return <mark className="translation-token time-token" key={`${part}-${index}`}>{part}</mark>;
    }
    return part;
  })}</>;
}

export function PromptSyntaxEditor({
  label,
  value,
  disabled = false,
  textareaRef,
  translation,
  translationState,
  translationStale = false,
  onChange,
  onRetryTranslation,
  onRequestEdit,
}: Props) {
  const id = useId();
  const [activeEdit, setActiveEdit] = useState<{ start: number; end: number; draft: string } | null>(null);
  const pendingEdit = useRef<{ start: number; end: number; draft: string } | null>(null);
  const sections = useMemo(() => promptSectionLinks(value), [value]);
  const bilingual = useMemo(() => buildBilingualPromptSections(value, translation), [translation, value]);
  const canStartEditing = !disabled || Boolean(onRequestEdit);

  useEffect(() => {
    if (disabled || !pendingEdit.current) return;
    setActiveEdit(pendingEdit.current);
    pendingEdit.current = null;
  }, [disabled]);

  useEffect(() => {
    if (!activeEdit) return;
    const frame = window.requestAnimationFrame(() => {
      const textarea = textareaRef.current;
      if (!textarea) return;
      textarea.focus();
      textarea.setSelectionRange(textarea.value.length, textarea.value.length);
    });
    return () => window.cancelAnimationFrame(frame);
  }, [activeEdit?.start, textareaRef]);

  function sentenceAt(index: number) {
    const section = bilingual.find((item) => index >= item.start && index < item.end) ?? bilingual[0];
    if (!section) return null;
    const sentence = pairBilingualPromptSentences(section.english, section.korean)[0];
    if (!sentence) return null;
    return { start: section.start + sentence.index, text: sentence.english };
  }

  async function beginEdit(start: number, text: string) {
    if (!canStartEditing) return;
    if (activeEdit) {
      textareaRef.current?.focus();
      return;
    }
    pendingEdit.current = { start, end: start + text.length, draft: text };
    if (disabled) {
      await onRequestEdit?.();
      return;
    }
    setActiveEdit(pendingEdit.current);
    pendingEdit.current = null;
  }

  function beginEditAt(index: number) {
    const target = sentenceAt(index);
    if (target) void beginEdit(target.start, target.text);
  }

  function saveActiveEdit() {
    if (!activeEdit) return;
    onChange(`${value.slice(0, activeEdit.start)}${activeEdit.draft}${value.slice(activeEdit.end)}`);
    setActiveEdit(null);
  }

  function cancelActiveEdit() {
    pendingEdit.current = null;
    setActiveEdit(null);
  }

  function handleSentenceKey(event: KeyboardEvent<HTMLDivElement>, start: number, text: string) {
    if (event.key !== "Enter" && event.key !== " ") return;
    event.preventDefault();
    void beginEdit(start, text);
  }

  return <div className="field prompt-field">
    <div className="prompt-field-heading">
      <label htmlFor={id}>{label}</label>
      <span><Braces size={13} aria-hidden="true" />색상과 한국어는 화면에만 표시됩니다</span>
    </div>
    <div className={`prompt-code-editor ${disabled && !onRequestEdit ? "disabled" : ""}`}>
      <div className="prompt-code-toolbar">
        <div className="prompt-code-legend" aria-label="프롬프트 색상 범례">
          {LEGEND.map(([kind, text]) => <span className={kind} key={kind}><i aria-hidden="true" />{text}</span>)}
        </div>
        <div className="prompt-editor-tools">
          {sections.length > 0 && <nav className="prompt-section-nav" aria-label="프롬프트 섹션 바로가기">
            {sections.map((section) => <button type="button" onClick={() => beginEditAt(section.index)} key={section.field}>{section.label}</button>)}
          </nav>}
          {activeEdit ? (
            <button className="prompt-edit-toggle active" type="button" onClick={saveActiveEdit}><Check size={13} />문장 편집 완료</button>
          ) : canStartEditing ? (
            <button className="prompt-edit-toggle" type="button" onClick={() => beginEditAt(0)}><PencilLine size={13} />문장 편집</button>
          ) : null}
        </div>
      </div>

      <div className="prompt-bilingual-body">
        {bilingual.map((section) => <section
          className="prompt-bilingual-section"
          id={`prompt-section-${section.key}`}
          key={section.key}
        >
          <header><strong>{section.label}</strong>{section.field && <code>{section.field}</code>}</header>
          <div className="prompt-sentence-list">
            {pairBilingualPromptSentences(section.english, translationState === "ready" ? section.korean : "").map((sentence, index) => {
              const sentenceStart = section.start + sentence.index;
              const isEditing = activeEdit?.start === sentenceStart;
              return <div
              className={`prompt-sentence-pair ${canStartEditing ? "editable" : ""} ${isEditing ? "editing" : ""}`}
              role={canStartEditing && !isEditing ? "button" : undefined}
              tabIndex={canStartEditing && !isEditing ? 0 : undefined}
              aria-label={canStartEditing && !isEditing ? `${section.label} ${index + 1}번 영어 문장 직접 편집` : undefined}
              onClick={isEditing ? undefined : () => void beginEdit(sentenceStart, sentence.english)}
              onKeyDown={isEditing ? undefined : (event) => handleSentenceKey(event, sentenceStart, sentence.english)}
              key={`${section.key}-${sentence.index}`}
            >
              {isEditing ? <div className="prompt-inline-editor" onClick={(event) => event.stopPropagation()}>
                <textarea
                  id={id}
                  ref={textareaRef}
                  value={activeEdit.draft}
                  onChange={(event) => setActiveEdit({ ...activeEdit, draft: event.target.value })}
                  onKeyDown={(event) => {
                    event.stopPropagation();
                    if (event.key === "Escape") cancelActiveEdit();
                    if (event.key === "Enter" && (event.metaKey || event.ctrlKey)) saveActiveEdit();
                  }}
                  rows={Math.max(2, Math.min(8, Math.ceil(activeEdit.draft.length / 90)))}
                  spellCheck={false}
                  aria-label={`${section.label} ${index + 1}번 영어 문장 편집`}
                />
                <div>
                  <span><kbd>⌘</kbd><kbd>Enter</kbd> 저장</span>
                  <button type="button" onClick={cancelActiveEdit}>취소</button>
                  <button type="button" onClick={saveActiveEdit}>완료</button>
                </div>
              </div> : <p className="prompt-english"><SyntaxText value={sentence.english} /></p>}
              <div className="prompt-korean">
                <span><Languages size={13} aria-hidden="true" />한글</span>
                {translationState === "loading" ? (
                  <div className="inline-translation-skeleton" aria-label="한국어 해석 중"><i /><i /></div>
                ) : sentence.korean ? (
                  <p><KoreanText value={sentence.korean} /></p>
                ) : translationState !== "error" ? (
                  <p className="translation-missing">이 문장의 해석이 없습니다.</p>
                ) : null}
              </div>
            </div>})}
          </div>
        </section>)}
        {translationStale && <p className="bilingual-translation-note">영어가 수정되었습니다. 저장하면 바로 아래 한국어도 새 원문 기준으로 갱신됩니다.</p>}
        {translationState === "error" && <div className="bilingual-translation-error"><p>{translation}</p><button type="button" onClick={(event) => { event.stopPropagation(); onRetryTranslation(); }}><RefreshCw size={13} />다시 해석</button></div>}
      </div>
    </div>
  </div>;
}
